// Page Détail Prestation - Fiche complète d'une prestation
// Flow : Voir → Indiquer quantité → Demander un devis (pas de paiement direct)

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Star, MapPin, Clock, Euro, Shield, CheckCircle, MessageCircle,
  Store, FileText, Phone, ChevronRight, Loader2, Wrench, Zap, Droplets,
  Flame, Home, Brush, Hammer, Building2, Award, Calendar, User, Send,
  Plus, Minus, AlertCircle, X
} from 'lucide-react';
import { useDemoMode } from '../../../components/demo/DemoModeProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de métiers avec icônes
const METIERS_ICONS = {
  plomberie: { icon: Droplets, color: '#0EA5E9', label: 'Plomberie' },
  electricite: { icon: Zap, color: '#F59E0B', label: 'Électricité' },
  maconnerie: { icon: Building2, color: '#6B7280', label: 'Maçonnerie' },
  carrelage: { icon: Wrench, color: '#10B981', label: 'Carrelage' },
  peinture: { icon: Brush, color: '#EC4899', label: 'Peinture' },
  menuiserie: { icon: Hammer, color: '#92400E', label: 'Menuiserie' },
  couverture: { icon: Home, color: '#7C3AED', label: 'Couverture' },
  chauffage: { icon: Flame, color: '#EF4444', label: 'Chauffage' },
};

// Labels tarification
const TARIF_LABELS = {
  forfait: { label: 'Forfait', unit: '€', description: 'Prix fixe' },
  horaire: { label: 'Tarif horaire', unit: '€/h', description: 'Par heure de travail' },
  metre_carre: { label: 'Au m²', unit: '€/m²', description: 'Par mètre carré' },
  sur_devis: { label: 'Sur devis', unit: '', description: 'Tarif personnalisé après étude' },
  gratuit: { label: 'Gratuit', unit: '', description: 'Estimation gratuite' },
};

// Mock data pour une prestation
const MOCK_PRESTATION = {
  id: 'presta-1',
  name: 'Pose de carrelage sol',
  description_short: 'Pose de carrelage sol et mur. Tous formats, tous styles.',
  description_long: `Service complet de pose de carrelage pour sols et murs. 

**Ce qui est inclus :**
- Préparation du support (ragréage si nécessaire)
- Pose du carrelage selon le motif choisi
- Joints de qualité professionnelle
- Nettoyage du chantier

**Types de carrelage acceptés :**
- Grès cérame
- Faïence
- Pierre naturelle
- Grands formats (jusqu'à 120x120)

**Garantie décennale** sur tous nos travaux.`,
  metier: 'carrelage',
  tarif_type: 'metre_carre',
  tarif_montant: 45,
  tarif_unite: '€/m²',
  tarif_min: 35,
  tarif_max: 65,
  duration: '2-5 jours selon surface',
  photos: [
    'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
    'https://images.unsplash.com/photo-1600566753376-12c8ab7a0fa3?w=800',
  ],
  certifications: ['qualibat', 'rge'],
  urgent_available: false,
  zone_intervention: '50 km autour de Toulouse',
  artisan: {
    id: 'artisan-3',
    shop_name: 'Carrelage Expert Occitanie',
    owner_name: 'Jean-Pierre Martin',
    city: 'Toulouse',
    postal_code: '31000',
    rating: 4.7,
    reviews_count: 64,
    is_verified: true,
    response_time: '< 2h',
    member_since: '2019',
    completed_jobs: 312,
    avatar: null,
  },
};

// Modal de demande de devis
function DevisRequestModal({ isOpen, onClose, prestation, quantity, estimatedPrice }) {
  const [formData, setFormData] = useState({
    description: '',
    address: '',
    city: '',
    postal_code: '',
    preferred_date: '',
    phone: '',
    photos: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token') || localStorage.getItem('client_token');
      
      // Récupérer le nom du client depuis le localStorage
      let clientName = '';
      let clientEmail = '';
      try {
        const userData = JSON.parse(localStorage.getItem('ecosystem_user') || localStorage.getItem('buyer_user') || '{}');
        clientName = userData.name || userData.full_name || '';
        clientEmail = userData.email || '';
      } catch (e) {}
      
      const requestBody = {
        prestation_id: prestation.id,
        prestation_name: prestation.name,
        artisan_id: prestation.artisan?.id || prestation.shop_id || '',
        artisan_shop_name: prestation.artisan?.shop_name || prestation.shop_name || '',
        quantity: quantity,
        quantity_unit: prestation.tarif_type === 'metre_carre' ? 'm²' : prestation.tarif_type === 'horaire' ? 'h' : 'unité',
        estimated_price: estimatedPrice,
        description: formData.description,
        address: formData.address,
        city: formData.city,
        postal_code: formData.postal_code,
        preferred_date: formData.preferred_date || null,
        phone: formData.phone,
        client_name: clientName,
        client_email: clientEmail,
      };

      const response = await fetch(`${API_URL}/api/artisans/devis-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
          // Reset form
          setFormData({
            description: '',
            address: '',
            city: '',
            postal_code: '',
            preferred_date: '',
            phone: '',
            photos: [],
          });
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de l\'envoi de la demande');
      }
    } catch (err) {
      console.error('Error submitting devis request:', err);
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Demander un devis</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={20} className="text-gray-500" />
            </button>
          </div>

          {success ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Demande envoyée !</h3>
              <p className="text-gray-500 text-sm">Le professionnel va étudier votre demande et vous recontacter rapidement.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 overflow-y-auto max-h-[70vh]">
              {/* Récapitulatif */}
              <div className="bg-orange-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-gray-900">{prestation.name}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-gray-600">
                    {quantity} {prestation.tarif_type === 'metre_carre' ? 'm²' : prestation.tarif_type === 'horaire' ? 'h' : 'unité(s)'}
                  </span>
                  <span className="font-bold text-orange-600">
                    {estimatedPrice > 0 ? `≈ ${estimatedPrice.toLocaleString()}€` : 'Sur devis'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">* Prix estimatif, devis définitif après étude du chantier</p>
              </div>

              {/* Description du besoin */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Décrivez votre projet *
                </label>
                <textarea
                  required
                  rows={3}
                  placeholder="Ex: Je souhaite refaire le carrelage de ma salle de bain de 12m²..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Adresse */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adresse du chantier *</label>
                  <input
                    type="text"
                    required
                    placeholder="12 rue de la Paix"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code postal *</label>
                  <input
                    type="text"
                    required
                    placeholder="31000"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville *</label>
                  <input
                    type="text"
                    required
                    placeholder="Toulouse"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              {/* Date souhaitée */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date souhaitée</label>
                <input
                  type="date"
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Téléphone */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                <input
                  type="tel"
                  required
                  placeholder="06 12 34 56 78"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Info */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl mb-4">
                <AlertCircle size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800">
                  Le professionnel étudiera votre demande et vous proposera un devis personnalisé après visite du chantier si nécessaire.
                </p>
              </div>

              {/* Message d'erreur */}
              {error && (
                <div className="flex items-start gap-3 p-3 bg-red-50 rounded-xl mb-4">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-800">{error}</p>
                </div>
              )}

              {/* Bouton submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-orange-500 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    <Send size={18} />
                    Envoyer ma demande de devis
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function PrestationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { requireAuth } = useDemoMode();
  
  const [prestation, setPrestation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showDevisModal, setShowDevisModal] = useState(false);

  // Charger la prestation
  useEffect(() => {
    const fetchPrestation = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/artisans/prestations/${id}`);
        if (response.ok) {
          const data = await response.json();
          setPrestation(data);
        } else {
          // Fallback aux données démo
          setPrestation(MOCK_PRESTATION);
        }
      } catch (error) {
        console.error('Error fetching prestation:', error);
        setPrestation(MOCK_PRESTATION);
      } finally {
        setLoading(false);
      }
    };

    fetchPrestation();
  }, [id]);

  // Calculer le prix estimé
  const calculateEstimatedPrice = () => {
    if (!prestation) return 0;
    if (prestation.tarif_type === 'sur_devis' || prestation.tarif_type === 'gratuit') return 0;
    return prestation.tarif_montant * quantity;
  };

  // Obtenir l'unité de quantité
  const getQuantityLabel = () => {
    if (!prestation) return 'unité(s)';
    switch (prestation.tarif_type) {
      case 'metre_carre': return 'm²';
      case 'horaire': return 'heure(s)';
      default: return 'unité(s)';
    }
  };

  // Contacter le pro
  const handleContact = () => {
    if (requireAuth()) {
      navigate(`/messages?to=${prestation.artisan.id}`);
    }
  };

  // Voir la boutique
  const handleViewShop = () => {
    navigate(`/boutiques/${prestation.artisan.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (!prestation) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <Wrench size={48} className="text-gray-300 mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Prestation non trouvée</h1>
        <button onClick={() => navigate(-1)} className="text-orange-600 font-medium">
          Retour
        </button>
      </div>
    );
  }

  const metierInfo = METIERS_ICONS[prestation.metier] || { icon: Wrench, color: '#6B7280', label: 'Service' };
  const MetierIcon = metierInfo.icon;
  const tarifInfo = TARIF_LABELS[prestation.tarif_type] || TARIF_LABELS.sur_devis;
  const estimatedPrice = calculateEstimatedPrice();

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header sticky */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center gap-3 h-14 px-4">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft size={22} className="text-gray-700" />
          </button>
          <h1 className="text-base font-bold text-gray-900 truncate flex-1">{prestation.name}</h1>
        </div>
      </header>

      {/* Photos */}
      <div className="relative bg-gray-200 aspect-[4/3]">
        {prestation.photos && prestation.photos.length > 0 ? (
          <>
            <img 
              src={prestation.photos[activePhoto]} 
              alt={prestation.name}
              className="w-full h-full object-cover"
            />
            {/* Indicateurs */}
            {prestation.photos.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {prestation.photos.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActivePhoto(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === activePhoto ? 'bg-white w-4' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MetierIcon size={64} className="text-gray-300" />
          </div>
        )}
        
        {/* Badge métier */}
        <div 
          className="absolute top-3 left-3 px-3 py-1.5 rounded-full text-xs font-bold text-white flex items-center gap-1.5"
          style={{ backgroundColor: metierInfo.color }}
        >
          <MetierIcon size={14} />
          {metierInfo.label}
        </div>

        {/* Badge urgent */}
        {prestation.urgent_available && (
          <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold bg-red-500 text-white flex items-center gap-1">
            <Zap size={12} />
            Urgence dispo
          </div>
        )}
      </div>

      {/* Contenu */}
      <div className="p-4">
        {/* Titre et prix */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{prestation.name}</h2>
          <p className="text-gray-600 text-sm mb-4">{prestation.description_short}</p>
          
          {/* Prix */}
          <div className="flex items-baseline gap-2 mb-2">
            {prestation.tarif_type === 'sur_devis' ? (
              <span className="text-2xl font-bold text-[#1E3A5F]">Sur devis</span>
            ) : prestation.tarif_type === 'gratuit' ? (
              <span className="text-2xl font-bold text-green-600">Gratuit</span>
            ) : (
              <>
                <span className="text-2xl font-bold text-[#1E3A5F]">{prestation.tarif_montant}€</span>
                <span className="text-gray-500">/{tarifInfo.unit.replace('€/', '')}</span>
              </>
            )}
          </div>
          <p className="text-xs text-gray-500">{tarifInfo.description}</p>
          
          {/* Durée */}
          {prestation.duration && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
              <Clock size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">Durée estimée : {prestation.duration}</span>
            </div>
          )}
        </div>

        {/* Sélecteur de quantité (si applicable) */}
        {prestation.tarif_type !== 'sur_devis' && prestation.tarif_type !== 'gratuit' && prestation.tarif_type !== 'forfait' && (
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Quantité estimée ({getQuantityLabel()})
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-gray-100 transition-colors"
                >
                  <Minus size={18} className="text-gray-600" />
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center text-lg font-bold focus:outline-none"
                />
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-3 hover:bg-gray-100 transition-colors"
                >
                  <Plus size={18} className="text-gray-600" />
                </button>
              </div>
              
              {/* Prix estimé */}
              <div className="flex-1 text-right">
                <p className="text-xs text-gray-500">Prix estimé</p>
                <p className="text-xl font-bold text-orange-600">≈ {estimatedPrice.toLocaleString()}€</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">* Le prix définitif sera confirmé après étude du chantier</p>
          </div>
        )}

        {/* Description détaillée */}
        {prestation.description_long && (
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
            <h3 className="font-bold text-gray-900 mb-3">Description</h3>
            <div className="text-sm text-gray-600 whitespace-pre-line">
              {prestation.description_long}
            </div>
          </div>
        )}

        {/* Certifications */}
        {prestation.certifications && prestation.certifications.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
            <h3 className="font-bold text-gray-900 mb-3">Certifications</h3>
            <div className="flex flex-wrap gap-2">
              {prestation.certifications.map((cert, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-full text-sm">
                  <Shield size={14} />
                  <span className="font-medium uppercase">{cert}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Artisan */}
        <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
          <h3 className="font-bold text-gray-900 mb-3">Prestataire</h3>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-[#1E3A5F] flex items-center justify-center">
              {prestation.artisan.avatar ? (
                <img src={prestation.artisan.avatar} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <Building2 size={24} className="text-white" />
              )}
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900">{prestation.artisan.shop_name}</p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <MapPin size={14} />
                <span>{prestation.artisan.city}</span>
                {prestation.artisan.is_verified && (
                  <CheckCircle size={14} className="text-emerald-500" />
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1">
                <Star size={16} className="text-yellow-400 fill-yellow-400" />
                <span className="font-bold text-gray-900">{prestation.artisan.rating}</span>
              </div>
              <p className="text-xs text-gray-500">{prestation.artisan.reviews_count} avis</p>
            </div>
          </div>
        </div>

        {/* Zone d'intervention */}
        {prestation.zone_intervention && (
          <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
            <div className="flex items-center gap-3">
              <MapPin size={20} className="text-orange-500" />
              <div>
                <p className="font-medium text-gray-900">Zone d'intervention</p>
                <p className="text-sm text-gray-500">{prestation.zone_intervention}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Barre d'actions fixe - 3 boutons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-40">
        <div className="flex gap-2 max-w-lg mx-auto">
          {/* Voir la boutique */}
          <button
            onClick={handleViewShop}
            className="flex-1 py-3 border-2 border-orange-500 bg-orange-50 text-orange-600 rounded-xl font-bold flex flex-col items-center justify-center gap-0.5 hover:bg-orange-100 transition-colors text-xs"
          >
            <Store size={18} />
            <span>Boutique</span>
          </button>
          
          {/* Contacter */}
          <button
            onClick={handleContact}
            className="flex-1 py-3 border-2 border-[#1E3A5F] text-[#1E3A5F] rounded-xl font-bold flex flex-col items-center justify-center gap-0.5 hover:bg-[#1E3A5F]/5 transition-colors text-xs"
          >
            <MessageCircle size={18} />
            <span>Contacter</span>
          </button>
          
          {/* Demander un devis */}
          <button
            onClick={() => {
              if (requireAuth()) {
                setShowDevisModal(true);
              }
            }}
            className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-bold flex flex-col items-center justify-center gap-0.5 hover:bg-orange-600 transition-colors text-xs"
          >
            <FileText size={18} />
            <span>Devis</span>
          </button>
        </div>
      </div>

      {/* Modal demande de devis */}
      <DevisRequestModal
        isOpen={showDevisModal}
        onClose={() => setShowDevisModal(false)}
        prestation={prestation}
        quantity={quantity}
        estimatedPrice={estimatedPrice}
      />
    </div>
  );
}
